import React, { useState, useEffect, useRef } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useNavigate } from 'react-router-dom';
import pierluigiCopertina1 from '../assets/pierluigi_girolami_copertina1.png';
import emilianoBattanzi from '../assets/emiliano_battazzi_copertina1.png';
import pierluigiCopertina2 from '../assets/pierluigi_girolami_copertina2.png';
import pierluigiCopertina3 from '../assets/pierluigi_girolami_copertina3.png';
import pierluigiCopertina4 from '../assets/pierluigi_girolami_copertina4.png';
import logo from '../assets/slide-3.png';
import fotoAntonella1 from '../assets/foto_antonella-1.JPG';
import fotoAntonella2 from '../assets/foto_antonella-2.JPG';
import fotoAntonella3 from '../assets/foto_antonella-3.JPG';
import fotoAntonella4 from '../assets/foto_antonella-4.JPG';
import fotoAntonella5 from '../assets/foto_antonella-5.JPG';
import fotoAntonella6 from '../assets/foto_antonella-6.JPG';
import fotoAntonella7 from '../assets/foto_antonella-7.jpg';
import fotoAntonella8 from '../assets/foto_antonella-8.jpg';
// Image carousel component
const ImageCarousel = ({ images, onOpenFullscreen }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const goToPrev = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };
  
  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  return (
    <div className="relative">
      <div className="overflow-hidden rounded-lg">
        <div className="flex items-center justify-center">
          <img 
            src={images[currentIndex]} 
            alt={`Foto ${currentIndex + 1}`} 
            className="h-64 object-contain cursor-pointer"
            onClick={() => onOpenFullscreen(currentIndex)}
          />
        </div>
      </div>
      
      <div className="flex items-center justify-between mt-4">
        <button 
          onClick={goToPrev}
          className="bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-primary/80 transition duration-300"
          aria-label="Foto precedente"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        
        <div className="text-center">
          <span className="text-gray-600">{currentIndex + 1} / {images.length}</span>
        </div>
        
        <button 
          onClick={goToNext}
          className="bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-primary/80 transition duration-300"
          aria-label="Foto successiva"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Fullscreen image modal
const FullscreenImageModal = ({ isOpen, onClose, images, initialIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  
  const goToPrev = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };
  
  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-300"
        aria-label="Chiudi"
      >
        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <button 
        onClick={goToPrev}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors duration-300"
        aria-label="Foto precedente"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-10 h-10">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>
      
      <div className="max-w-5xl max-h-screen p-4">
        <img 
          src={images[currentIndex]} 
          alt={`Foto ${currentIndex + 1}`} 
          className="max-h-[85vh] max-w-full object-contain"
        />
      </div>
      
      <button 
        onClick={goToNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors duration-300"
        aria-label="Foto successiva"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-10 h-10">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white">
        <span>{currentIndex + 1} / {images.length}</span>
      </div>
    </div>
  );
};

const Modal = ({ isOpen, onClose, title, children, primaryAction }) => {
  const modalRef = useRef(null);
  
  // Chiudi il modale quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  // Gestisce il tasto Escape per chiudere il modale
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"></div>
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl mx-auto z-10 transform transition-all ease-in-out duration-300 overflow-hidden"
        style={{ maxHeight: '95vh', width: '80%', maxWidth: '900px' }}
      >
        <div className="px-5 sm:px-6 md:px-8 py-4 border-b flex items-center justify-between">
          <h3 className="text-xl sm:text-2xl font-semibold text-primary">{title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Chiudi"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-5 sm:px-8 md:px-10 py-8 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 140px)', overflowX: 'hidden' }}>
          {children}
        </div>
        <div className="px-5 sm:px-8 md:px-10 py-4 border-t bg-gray-50 flex justify-end">
          {primaryAction}
        </div>
      </div>
    </div>
  );
};

const ImpactStory = ({ image, title, description, fullText, imageObjectPosition = "object-center", imageStyle }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [fullscreenInitialIndex, setFullscreenInitialIndex] = useState(0);
  
  const handleOpenFullscreen = (index) => {
    setFullscreenInitialIndex(index);
    setIsFullscreenOpen(true);
  };
  
  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <img src={image} alt={title} className={`w-full h-48 object-cover ${imageObjectPosition}`} style={imageStyle} />
        <div className="p-4">
          <h3 className="text-xl font-bold text-primary mb-2">{title}</h3>
          <p className="text-gray-700 mb-4">{description}</p>
          {fullText && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-primary text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-primary/90 transition duration-300"
            >
              Leggi tutto
            </button>
          )}
        </div>
      </div>
      
      {fullText && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={title}
          primaryAction={
            <button
              onClick={() => setIsModalOpen(false)}
              className="bg-primary text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary/90 transition duration-300"
            >
              Chiudi
            </button>
          }
        >
          <div className="prose max-w-none">
            {fullText.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-6 text-base md:text-lg text-gray-700" style={{ lineHeight: '1.8' }}>{paragraph}</p>
            ))}
            
            {title === "La storia di Pierluigi" && (
              <div className="mt-10 border-t pt-8">
                <h4 className="text-xl font-semibold text-primary mb-4">I libri di Pierluigi</h4>
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <div className="book-cover">
                    <img 
                      src={pierluigiCopertina1}
                      alt="Libro di Pierluigi Girolami" 
                      className="h-48 object-cover rounded-md shadow-md hover:shadow-lg transition-shadow duration-300"
                    />
                  </div>
                  <div className="book-cover">
                    <img 
                      src={pierluigiCopertina2} 
                      alt="Libro di Pierluigi Girolami" 
                      className="h-48 object-cover rounded-md shadow-md hover:shadow-lg transition-shadow duration-300"
                    />
                  </div>
                  <div className="book-cover">
                    <img 
                      src={pierluigiCopertina3}
                      alt="Libro di Pierluigi Girolami" 
                      className="h-48 object-cover rounded-md shadow-md hover:shadow-lg transition-shadow duration-300"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {title === "La storia di Antonella" && (
              <div className="mt-10 border-t pt-8">
                <h4 className="text-xl font-semibold text-primary mb-4">Le foto di Antonella</h4>
                <p className="text-gray-700 mb-6">La fotografia è la mia passione, un modo per catturare e condividere la bellezza del mondo. Ecco alcune delle mie foto. Clicca su un'immagine per ingrandirla.</p>
                <ImageCarousel 
                  images={[
                    fotoAntonella1, 
                    fotoAntonella2, 
                    fotoAntonella3, 
                    fotoAntonella4, 
                    fotoAntonella5, 
                    fotoAntonella6, 
                    fotoAntonella7, 
                    fotoAntonella8
                  ]} 
                  onOpenFullscreen={handleOpenFullscreen}
                />
              </div>
            )}
            {title === "La storia di Emiliano" && (
              <div className="mt-10 border-t pt-8">
                <h4 className="text-xl font-semibold text-primary mb-4">Il libro di Emiliano</h4>
                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <div className="book-cover">
                    <img 
                      src={emilianoBattanzi}
                      alt="Libro di Pierluigi Girolami" 
                      className="h-48 object-cover rounded-md shadow-md hover:shadow-lg transition-shadow duration-300"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
      
      {/* Fullscreen Image Modal */}
      {title === "La storia di Antonella" && fullText && (
        <FullscreenImageModal 
          isOpen={isFullscreenOpen}
          onClose={() => setIsFullscreenOpen(false)}
          images={[
            fotoAntonella1, 
            fotoAntonella2, 
            fotoAntonella3, 
            fotoAntonella4, 
            fotoAntonella5, 
            fotoAntonella6, 
            fotoAntonella7, 
            fotoAntonella8
          ]}
          initialIndex={fullscreenInitialIndex}
        />
      )}
    </>
  );
};
const DonationForm = () => {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isMonthly, setIsMonthly] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [error, setError] = useState(null);
  const [{ isPending }] = usePayPalScriptReducer();
  
  // Stati per i modali di conferma
  const [showSingleDonationModal, setShowSingleDonationModal] = useState(false);
  const [showRecurringDonationModal, setShowRecurringDonationModal] = useState(false);
  const [donationDetails, setDonationDetails] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (amount && name && email) {
      setShowPayPal(true);
    }
  };

  // Funzione per creare un ordine standard (donazione singola)
  const createOrder = (data, actions) => {
    try {
      // Reset any previous errors
      setError(null);
      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: amount,
              currency_code: "EUR"
            },
            description: `Donazione a Robin Hood Roma ODV - ${name} (${email})`
          }
        ],
        application_context: {
          shipping_preference: "NO_SHIPPING"
        }
      });
    } catch (err) {
      console.error('Errore nella creazione dell\'ordine:', err);
      setError('Si è verificato un errore nella configurazione della donazione. Riprova più tardi.');
      return null;
    }
  };

  // Funzione per creare una sottoscrizione (donazione ricorrente)
  const createSubscription = (data, actions) => {
    try {
      // Reset any previous errors
      setError(null);
      
      // IMPORTANTE: È necessario configurare un ID del piano valido nel dashboard PayPal
      // 1. Accedi al tuo account PayPal Developer
      // 2. Crea un piano di sottoscrizione nella sezione "Subscription Plans"
      // 3. Copia l'ID del piano (formato P-XXXXXXX) e sostituiscilo qui sotto
      const planId = 'P-XXXXXXXXXX';
      
      // Verifica se l'ID del piano è ancora il valore placeholder generico
      if (planId === 'P-XXXXXXXXXX') {
        console.error('ID del piano di sottoscrizione non configurato');
        setError('Errore di configurazione: È necessario impostare un ID del piano di sottoscrizione valido nel codice. Contatta l\'amministratore del sito.');
        return null;
      }
      
      // Verifica che l'utente abbia fornito i dati necessari per l'autenticazione
      if (!name || !email) {
        console.error('Dati utente mancanti per la sottoscrizione');
        setError('Per procedere con una donazione ricorrente è necessario fornire nome completo ed email.');
        return null;
      }
      
      return actions.subscription.create({
        plan_id: planId,
        subscriber: {
          name: {
            given_name: name.split(' ')[0],
            surname: name.split(' ').slice(1).join(' ')
          },
          email_address: email
        },
        application_context: {
          shipping_preference: "NO_SHIPPING",
          user_action: "SUBSCRIBE_NOW" // Richiede un'azione esplicita da parte dell'utente
        }
      });
    } catch (err) {
      console.error('Errore nella creazione della sottoscrizione:', err);
      setError('Si è verificato un errore nella configurazione della donazione mensile. Riprova più tardi.');
      return null;
    }
  };

  const onError = (err) => {
    console.error('Errore PayPal:', err);
    setError('Si è verificato un errore durante il processo di pagamento. Riprova più tardi.');
  };

  // Modifica la funzione onApprove nel componente DonationForm

const onApprove = (data, actions) => {
  // Reset any previous errors
  setError(null);
  
  console.log("Transazione approvata:", data);
  
  // Per le donazioni singole
  if (!isMonthly) {
    try {
      // In ambiente sandbox, potrebbe non essere possibile catturare l'ordine
      // quindi gestisci il caso in cui actions.order è disponibile o meno
      if (actions.order && actions.order.capture) {
        return actions.order.capture()
          .then(function (details) {
            // Mostra il modale di conferma invece dell'alert
            setDonationDetails({
              amount: amount,
              name: name,
              date: new Date().toLocaleDateString()
            });
            setShowSingleDonationModal(true);
            return details;
          })
          .catch(error => {
            console.error('Errore durante la cattura dell\'ordine:', error);
            setError('Si è verificato un errore durante l\'elaborazione della donazione. Riprova più tardi.');
            // In sandbox, potrebbe essere necessario comunque resettare il form
            resetForm();
            return Promise.reject(error);
          });
      } else {
        // Nel caso in cui actions.order.capture non sia disponibile (tipicamente in sandbox)
        console.log("Sandbox mode: impossibile catturare l'ordine automaticamente.");
        
        // Mostra il modale di conferma invece dell'alert
        setDonationDetails({
          amount: amount,
          name: name,
          date: new Date().toLocaleDateString(),
          sandbox: true
        });
        setShowSingleDonationModal(true);
        return Promise.resolve();
      }
    } catch (err) {
      console.error('Errore durante l\'approvazione:', err);
      setError('Si è verificato un errore durante l\'elaborazione della donazione. Riprova più tardi.');
      // In sandbox, potrebbe essere necessario comunque resettare il form
      resetForm();
      return Promise.resolve(); // Risolve comunque la promessa per evitare blocchi
    }
  } 
  // Per le donazioni ricorrenti
  else {
    try {
      // Verifica che data contenga le informazioni sulla sottoscrizione
      console.log("Dati sottoscrizione:", data);
      
      // Mostra il modale per donazione ricorrente invece dell'alert
      setDonationDetails({
        amount: amount,
        name: name,
        date: new Date().toLocaleDateString(),
        subscriptionId: data.subscriptionID || 'ID-Sandbox',
        sandbox: !data.subscriptionID
      });
      setShowRecurringDonationModal(true);
      return Promise.resolve();
    } catch (err) {
      console.error('Errore durante l\'approvazione della sottoscrizione:', err);
      setError('Si è verificato un errore durante l\'attivazione della donazione mensile. Riprova più tardi.');
      resetForm();
      return Promise.resolve(); // Risolve comunque la promessa per evitare blocchi
    }
  }
};

// Aggiungi questa funzione helper per resettare il form
const resetForm = () => {
  setAmount('');
  setName('');
  setEmail('');
  setIsMonthly(false);
  setShowPayPal(false);
  setError(null);
};

// Funzione per chiudere il modale di donazione singola e resettare il form
const handleSingleDonationModalClose = () => {
  setShowSingleDonationModal(false);
  resetForm();
};

// Funzione per chiudere il modale di donazione ricorrente e resettare il form
const handleRecurringDonationModalClose = () => {
  setShowRecurringDonationModal(false);
  resetForm();
};
// Aggiungi anche un gestore per gli eventi di cancellazione
const onCancel = () => {
  console.log('Pagamento annullato dall\'utente');
  setShowPayPal(false); // Torna indietro alla visualizzazione del form
};

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-xl font-semibold mb-4 text-primary">Fai una donazione</h3>
      
      {!showPayPal ? (
  <>
    <div className="mb-4">
      <label className="block text-gray-700 mb-2" htmlFor="amount">
        Importo (€)
      </label>
      <input
        id="amount"
        type="number"
        min="1"
        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        required
      />
    </div>
    
    {/* <div className="mb-4">
      <label className="flex items-center">
        <input
          type="checkbox"
          className="mr-2 text-primary focus:ring-primary"
          checked={isMonthly}
          onChange={(e) => setIsMonthly(e.target.checked)}
        />
        <span className="text-gray-700">Donazione mensile ricorrente</span>
      </label>
    </div> */}
    
    <div className="mb-4">
      <label className="block text-gray-700 mb-2" htmlFor="name">
        Nome e Cognome
      </label>
      <input
        id="name"
        type="text"
        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
    </div>
    
    <div className="mb-6">
      <label className="block text-gray-700 mb-2" htmlFor="email">
        Email
      </label>
      <input
        id="email"
        type="email"
        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
    </div>
    
    <button
      type="submit"
      className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-bold text-lg hover:bg-red-700 transition duration-300 uppercase"
    >
      DONA ORA!
    </button>
  </>
) : (
  <div className="space-y-4">
    <div className="p-4 bg-gray-50 rounded-lg">
      <p className="font-medium text-gray-700">Riepilogo donazione:</p>
      <p className="text-gray-600">Importo: <span className="font-semibold">€{amount}</span></p>
      <p className="text-gray-600">Tipo: <span className="font-semibold">{isMonthly ? 'Donazione mensile' : 'Donazione una tantum'}</span></p>
      <p className="text-gray-600">Donatore: <span className="font-semibold">{name}</span></p>
    </div>
    
    {/* Mostra i pulsanti PayPal solo se nessuna modale di conferma è aperta */}
    {!showSingleDonationModal && !showRecurringDonationModal && (
      <>
        {isPending ? (
          <div className="text-center py-4">
            <p>Caricamento PayPal...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
                <div className="font-medium">Attenzione!</div>
                <div>{error}</div>
              </div>
            )}
            <PayPalButtons
              // Usa createOrder solo per donazioni singole
              createOrder={!isMonthly ? createOrder : undefined}
              // Usa createSubscription solo per donazioni ricorrenti
              createSubscription={isMonthly ? createSubscription : undefined}
              onApprove={onApprove}
              onCancel={onCancel}
              onError={onError}
              style={{
                layout: "vertical",
                color: "blue",
                shape: "rect",
                label: "donate"
              }}
              // Aggiungi queste proprietà per l'ambiente sandbox
              forceReRender={[amount, name, email, isMonthly]}
              // Imposta a true solo per testing
              fundingSource={undefined}
            />
          </div>
        )}
      </>
    )}
    
    <button
      type="button"
      onClick={() => setShowPayPal(false)}
      className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-300 transition duration-300"
    >
      Modifica donazione
    </button>
  </div>
)}
      
      {/* Modale di conferma per donazione singola */}
      <Modal
        isOpen={showSingleDonationModal}
        onClose={handleSingleDonationModalClose}
        title="Donazione Completata"
        primaryAction={
          <button
            onClick={handleSingleDonationModalClose}
            className="bg-primary text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary/90 transition duration-300"
          >
            Chiudi
          </button>
        }
      >
        <div className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
              <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <p className="text-center text-lg text-gray-800">
            Grazie <span className="font-semibold">{donationDetails?.name}</span>!
          </p>
          <p className="text-center text-gray-700">
            La tua donazione di <span className="font-semibold">€{donationDetails?.amount}</span> è stata completata con successo.
          </p>
          {donationDetails?.sandbox && (
            <p className="text-center text-sm text-gray-500 mt-2">
              (Ambiente sandbox: questa è una transazione di test)
            </p>
          )}
          <p className="text-center text-gray-600 text-sm mt-6">
            Data: {donationDetails?.date}
          </p>
        </div>
      </Modal>

      {/* Modale di conferma per donazione ricorrente */}
      <Modal
        isOpen={showRecurringDonationModal}
        onClose={handleRecurringDonationModalClose}
        title="Donazione Mensile Attivata"
        primaryAction={
          <button
            onClick={handleRecurringDonationModalClose}
            className="bg-primary text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary/90 transition duration-300"
          >
            Chiudi
          </button>
        }
      >
        <div className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
              <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <p className="text-center text-lg text-gray-800">
            Grazie <span className="font-semibold">{donationDetails?.name}</span>!
          </p>
          <p className="text-center text-gray-700">
            La tua donazione mensile di <span className="font-semibold">€{donationDetails?.amount}</span> è stata attivata con successo.
          </p>
          {donationDetails?.sandbox && (
            <p className="text-center text-sm text-gray-500 mt-2">
              (Ambiente sandbox: questa è una transazione di test)
            </p>
          )}
          <p className="text-center text-gray-600 text-sm mt-2">
            ID Sottoscrizione: {donationDetails?.subscriptionId}
          </p>
          <p className="text-center text-gray-600 text-sm">
            Data: {donationDetails?.date}
          </p>
        </div>
      </Modal>
    </form>
  );
};

const VolunteerForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [area, setArea] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert("Grazie per il tuo interesse! Ti contatteremo presto.");
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-xl font-semibold mb-4 text-primary">Diventa Volontario</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="volunteer-name">
          Nome e Cognome
        </label>
        <input
          id="volunteer-name"
          type="text"
          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="volunteer-email">
          Email
        </label>
        <input
          id="volunteer-email"
          type="email"
          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="volunteer-phone">
          Telefono
        </label>
        <input
          id="volunteer-phone"
          type="tel"
          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 mb-2" htmlFor="volunteer-area">
          Area di Interesse
        </label>
        <select
          id="volunteer-area"
          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          required
        >
          <option value="">Seleziona un'area</option>
          <option value="eventi">Organizzazione Eventi</option>
          <option value="raccolta-fondi">Raccolta Fondi</option>
          <option value="supporto-famiglie">Supporto Famiglie</option>
          <option value="comunicazione">Comunicazione</option>
          <option value="amministrazione">Amministrazione</option>
          <option value="altro">Altro</option>
        </select>
      </div>
      
      <div className="mb-6">
        <label className="block text-gray-700 mb-2" htmlFor="volunteer-message">
          Messaggio
        </label>
        <textarea
          id="volunteer-message"
          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary h-32"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Raccontaci qualcosa di te e perché vuoi unirti a noi..."
        />
      </div>
      
      <button
        type="submit"
        className="w-full bg-primary text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary/90 transition duration-300"
      >
        Invia Richiesta
      </button>
    </form>
  );
};

const Sostienici = () => {
  const navigate = useNavigate();
  return (
    <>
      {/* Hero Section */}
      <div className="bg-red-600 text-white py-20 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 uppercase">Sostieni Robin Hood Roma ODV</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto font-semibold">
            Il tuo supporto può fare la differenza nella vita dei bambini e adolescenti 
            affetti da malattie ematologiche rare.
          </p>
        </div>
      </div>

      {/* Sezione Donazioni - NUOVA POSIZIONE 1 */}
      <section id="donazioni" className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8 text-red-600">
            Fai una Donazione
          </h2>
          <p className="text-center text-lg max-w-3xl mx-auto mb-12">
            Ogni donazione, grande o piccola, contribuisce a fare la differenza nella vita dei bambini e adolescenti con malattie ematologiche rare.
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
            <div>
              {/* Scommentare la donation form per attivarla, nel momento in cui abbiamo il client ID paypal del cliente */}
              { <DonationForm /> }
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold mb-4 text-blue-700 text-center" style={{ fontVariant: 'small-caps' }}>Altre modalità di donazione</h3>
              <div className="mb-6">
                <h4 className="text-xl font-bold text-blue-700 mb-2">Bonifico Bancario</h4>
                <p className="text-gray-700 mb-2">Intestato a: Associazione Robin Hood Roma ODV</p>
                <p className="text-gray-700 mb-2">IBAN: <span className="text-red-600 font-semibold">IT12A0123456789000000123456</span></p>
                <p className="text-gray-700 mb-4">Causale: Donazione liberale</p>
              </div>
              <div className="mb-6">
                <h4 className="text-xl font-bold text-blue-700 mb-2">5x1000</h4>
                <p className="text-gray-700 mb-4">Dona il tuo 5x1000 a Robin Hood Roma ODV, non ti costa nulla ma per noi significa tanto.</p>
                <p className="text-gray-700 mb-2">Codice Fiscale: <span className="text-red-600 font-semibold">91234567890</span></p>
              </div>
              
              <div className="mb-6">
                <h4 className="font-medium text-lg mb-2 text-blue-700">Altre donazioni!</h4>
                <p className="text-gray-700">
                  Accettiamo anche donazioni di beni e servizi. Contattaci per maggiori informazioni su come donare materiali o offrire servizi professionali.
                </p>
              </div>
          </div>
        </div>
      </div>
      </section>

      {/* Come utilizziamo i fondi - NUOVA POSIZIONE 2 */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-blue-700">
            Come Utilizziamo i Fondi
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-primary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Sostegno alla ricerca e alla conoscenza delle malattie rare ematologiche</h3>
              <p className="text-gray-600">
                Il 25% dei fondi raccolti è stato finora destinato al sostegno della ricerca e diffusione
                delle conoscenze sulle malattie ematologiche rare.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-primary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Supporto alle Famiglie</h3>
              <p className="text-gray-600">
                Il 30% dei fondi viene utilizzato per fornire supporto economico, logistico e psicologico 
                alle famiglie dei pazienti durante il percorso di cura.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="bg-primary/10 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-primary">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-primary">Acquisto Attrezzature</h3>
              <p className="text-gray-600">
                Il 45% dei fondi raccolti è stato finora investito nell’acquisto di attrezzature (elettrocardiografi, reagenti)
                ed altro materiale destinati a garantire l’assistenza ai pazienti con malattie rare ematologiche seguiti
                in Ematologia, AOU Policlinico Umberto I.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Storie di Impatto - NUOVA POSIZIONE 3 */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-blue-700">
            Storie di Impatto
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ImpactStory
              image={pierluigiCopertina4}
              title="La storia di Pierluigi"
              description="Nel 1987 sono stato ricoverato in Ematologia, Policlinico Umberto I di Roma per una rara forma di leucemia acuta. A distanza di anni, mi ricordo ancora la sensazione che ho avuto, appena varcata la soglia: sentirmi completamente a mio agio, come se fossi a casa."
              fullText={`Nel 1987 sono stato ricoverato in Ematologia, Policlinico Umberto I di Roma per una rara forma di leucemia acuta. A distanza di anni, mi ricordo ancora la sensazione che ho avuto, appena varcata la soglia: sentirmi completamente a mio agio, come se fossi a casa.

La mia frequentazione, in reparto e in ambulatorio, mi ha confermato questa sensazione, dovuta al calore umano di tutto il personale e dei volontari. Sono rimasto colpito anche dall'organizzazione, e dall'abnegazione dei medici e infermieri, disponibili e attivi con competenza nel non facile impegno di alleviare le nostre sofferenze. Una professionalità e umanità determinante per la serenità dei pazienti e dei genitori.

Si sa che ogni individuo ha il suo modo di affrontare la malattia; la mia singolare esperienza mi ha permesso di fronteggiarla con positività e determinazione. Oggi, dopo quasi quarant'anni, sento una gratitudine nei confronti della vita, che, attraverso questa malattia, mi ha dato l'opportunità di ricominciare a vivere con un altro spirito.

Scrivere … con la mano che volteggiava sui fogli bianchi è stata per me una sorgente di riflessione, mentre come un equilibrista vagavo sul filo labile della vita. Ho scritto fiumi di parole che mi hanno aiutato ad entrare nel mio mondo interiore, donandomi una chiave di lettura nell'interpretazione del vero senso della vita. Ho pubblicato tre libri, ma continuo a scrivere.

Oggi ho il privilegio di essere un socio e volontario dell'associazione Robin Hood Roma ODV, un'Associazione di pazienti per i pazienti. Un ritorno a casa, onorato e felice di poter dare il mio contributo con sensibilità e riconoscenza alla Vita.

Pierluigi Girolami`}
            />
            <ImpactStory
              image={fotoAntonella1}
              title="La storia di Antonella"
              description="Sono Antonella, e sono una paziente dell'Ematologia. Lo sono da abbastanza tempo da esser cresciuta attraverso tutti i suoi cambiamenti nel corso degli anni. La fotografia è la mia passione e attraverso di essa trasmetto l'amore per la vita."
              fullText={`Sono Antonella, e sono una paziente dell'Ematologia. Lo sono da abbastanza tempo da esser cresciuta attraverso tutti i suoi cambiamenti nel corso degli anni. Avere una malattia rara (congenita) non rende la vita facile. Spesso le rinunce sono tante. Ma il tempo è uno straordinario alleato.

Quello che la Ricerca è riuscita a realizzare nell'ultimo decennio è straordinario. Da gran parte delle malattie rare non si guarisce (ancora) ma il lavoro, la dedizione e la tenacia di medici e ricercatori sono la speranza di noi pazienti. Da quando ero piccolissima, loro sono stati la mia seconda famiglia. Lo sono ancora oggi.

L'amore per la vita, che la malattia ha solo rafforzato, cerco di trasmetterlo attraverso la fotografia, che è la mia passione. E le cose belle vanno sempre condivise perché possano germogliare nel cuore degli altri. Per me la fotografia è 'fermare' la realtà. Ho iniziato a scattar foto più di vent'anni fa, quasi per caso.

Camminare e fotografare fa bene al fisico e alla mente. Le foto sono istanti fermati per sempre. Un frammento di vita che fa rivivere l'emozione di quel momento passato, nel tempo. Basta saperli cogliere e farne tesoro.`}
            />
          <ImpactStory
              image={emilianoBattanzi}
              title="La storia di Emiliano"
              description="Tutto è iniziato da bambino, nelle sale d’attesa dell’Ematologia Pediatrica a Roma, dove ho scoperto una passione totalizzante per il calcio. Quelle letture sono diventate il seme di un percorso che mi ha portato a scrivere Calcio liquido."
              fullText={`Non so quante ore debba aver passato, da bambino, nella sala d’attesa e nelle varie stanze dell’Ematologia Pediatrica dell’Ematologia di Via Benevento 6 di Roma, di sicuro meno di 10mila, ma in qualcosa sono diventato bravo. A leggere, soprattutto: mi portavo sempre qualche giornale e rivista da leggere. Di solito, riviste sportive, ad esempio il Guerin Sportivo.
Secondo la celebre teoria delle 10mila ore, formulata dal sociologo canadese Malcolm Gladwell, per diventare bravi in qualcosa bisogna applicarsi intensamente, per circa 10mila ore, appunto.
Se questo è vero sono diventato bravo nel calcio, so tutto di quello che è stato scritto. Ogni fotogramma è nella mia testa. So cosa hanno scritto le penne più importanti. Anche oggi, che sono nel settore giornalistico calcistico, so che la mia passione quasi maniacale per il calcio è nata lì tra le cure, le attenzioni ricevute in Ematologia Pediatrica del Policlinico Umberto I di Roma.
Trent’anni dopo il mio primo ingresso a Via Benevento 6, è uscito nelle librerie, pubblicato dall’editore 66thand2nd, il mio libro Calcio liquido. L’evoluzione tattica della Serie A.
Il libro parte da un aforisma del celebre allenatore Vujadin Boškov, che recita così: “i giocatori vincono, gli allenatori perdono”. Calcio liquido racconta la storia del calcio vista dalla parte degli allenatori, ripercorrendo gli ultimi trent’anni di evoluzione tattica della Serie A, partendo dalla rivoluzione di Arrigo Sacchi. Il libro ripercorre, con un linguaggio chiaro e accessibile, i momenti decisivi delle grandi squadre italiane e le idee degli allenatori più importanti del campionato: da Sacchi a Capello, da Zeman a Lippi, da Spalletti a Mourinho, da Ancelotti a Sarri, fino a Conte, Gasperini, Allegri, Inzaghi e Mancini. Modi diversi di pensare il calcio. In ogni fase in cui il movimento calcistico italiano sembra arretrato, si intravede una speranza di rinnovamento e di successo futuro.

La teoria delle 10mila ore di Gladwell è poi stata smentita dalla letteratura scientifica, ma per me è ancora valida. Durante le lunghe attese in Ematologia ho considerato la lettura di argomenti riguardanti il calcio una dolce compagnia che mi ha poi permesso di poter esprimere quanto recepito e fatto mio. Alla pubblicazione del mio libro, ho voluto condividere questo evento emozionante con i medici che mi avevano seguito in un momento impegnativo della mia infanzia. E’ stato, poi, naturale per me essere coinvolto e sostenere l’Associazione Robin Hood Roma, per restituire un po’ di quello che sento di aver ricevuto.`}
              imageStyle={{ objectPosition: 'center 35%' }}
            />
          </div>
        </div>
      </section>
      
      {/* Call to Action per Volontariato - NUOVA POSIZIONE 4 */}
      <section className="py-16 px-4 bg-primary/10">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-blue-700">Vuoi Fare la Differenza?</h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Oltre alle donazioni, puoi supportare la nostra missione diventando volontario.
          </p>
          <button
            onClick={() => navigate('/volontari')}
            className="bg-primary text-white py-3 px-8 rounded-full text-lg font-bold hover:bg-primary/90 transition duration-300"
          >
            Diventa Volontario
          </button>
          <button
              onClick={() => navigate('/soci')}
              className="ml-16 bg-accent text-white py-3 px-8 rounded-full text-lg font-bold hover:bg-accent/90 transition duration-300"
            >
              Diventa Socio
            </button>
        </div>
      </section>
    </>
  );
};

export default Sostienici;
